import React, { useEffect, useState, useCallback } from "react";
import { Users, Loader2, Plus, Trash2, ShieldCheck, Building2, Crown, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { Avatar } from "../components/Avatar";

const ROLES = [["owner", "Propriétaire"], ["agent", "Agent"], ["member", "Membre"], ["client", "Client"]];
const ROLE_COLOR = { owner: "#F59E0B", agent: "#3B82F6", member: "#10B981", client: "#8B5CF6" };
const roleLabel = (r) => (ROLES.find((x) => x[0] === r) || [r, r])[1];

export default function MembersPage() {
  const { managed, selectedId } = useCompany();
  const [cid, setCid] = useState(selectedId !== "all" ? selectedId : (managed[0]?.id || ""));
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [myRole, setMyRole] = useState(null);
  const [invite, setInvite] = useState({ email: "", name: "", role: "member", hourly_rate: 0 });
  const [inviting, setInviting] = useState(false);

  useEffect(() => { if (!cid && managed[0]) setCid(managed[0].id); }, [managed, cid]);
  useEffect(() => { if (selectedId !== "all") setCid(selectedId); }, [selectedId]);

  const load = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const co = managed.find((c) => c.id === cid);
      setMyRole(co?.role || null);
      const { data } = await api.get(`/companies/${cid}/members`);
      setMembers(data || []);
    } catch (err) { setMembers([]); toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [cid, managed]);
  useEffect(() => { load(); }, [load]);

  const isOwner = myRole === "owner";

  const patchMember = async (m, patch) => {
    setMembers((ms) => ms.map((x) => x.id === m.id ? { ...x, ...patch } : x));
    try { await api.patch(`/companies/${cid}/members/${m.id}`, patch); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Retirer ${m.name} de l'entreprise ?`)) return;
    try { await api.delete(`/companies/${cid}/members/${m.id}`); toast.success("Membre retiré"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doInvite = async () => {
    if (!invite.email.trim()) { toast.error("Email requis"); return; }
    setInviting(true);
    try { await api.post(`/companies/${cid}/members`, invite); toast.success("Membre ajouté"); setInvite({ email: "", name: "", role: "member", hourly_rate: 0 }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setInviting(false); }
  };

  return (
    <div className="h-full flex flex-col" data-testid="members-page">
      <div className="h-14 px-4 flex items-center gap-3 border-b border-white/10 shrink-0">
        <ShieldCheck size={18} className="text-klein" />
        <h1 className="font-cabinet font-bold text-lg">Membres & rôles</h1>
        <div className="flex-1" />
        <div className="flex items-center gap-2 bg-background/50 border border-white/10 rounded-lg px-3 h-9">
          <Building2 size={14} className="text-zinc-500" />
          <select data-testid="members-company" value={cid} onChange={(e) => setCid(e.target.value)} className="bg-transparent text-sm focus:outline-none pr-2">
            {managed.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 max-w-4xl w-full mx-auto">
        {managed.length === 0 ? <div className="text-center py-16 text-zinc-500 text-sm">Vous ne gérez aucune entreprise.</div>
          : loading ? <div className="flex justify-center py-16 text-zinc-400"><Loader2 className="animate-spin" /></div>
          : (
            <>
              {isOwner && (
                <div className="rounded-2xl border border-white/10 bg-card p-4 mb-5" data-testid="members-invite">
                  <div className="flex items-center gap-2 mb-3"><Plus size={16} className="text-klein" /><h3 className="font-cabinet font-bold text-sm">Inviter un membre</h3></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                    <div className="lg:col-span-2 flex items-center gap-2 fd-input"><Mail size={14} className="text-zinc-500 shrink-0" /><input data-testid="invite-email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder="email@client.com" className="flex-1 bg-transparent focus:outline-none text-sm" /></div>
                    <input data-testid="invite-name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} placeholder="Nom (optionnel)" className="fd-input" />
                    <select data-testid="invite-role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} className="fd-input">{ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-2.5">
                    <div className="flex items-center gap-2 text-xs text-zinc-400">Taux horaire <input data-testid="invite-rate" type="number" value={invite.hourly_rate} onChange={(e) => setInvite({ ...invite, hourly_rate: parseFloat(e.target.value) || 0 })} className="w-20 fd-input !h-8" /> €/h</div>
                    <button data-testid="invite-submit" onClick={doInvite} disabled={inviting} className="h-9 px-4 rounded-full bg-klein text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-1.5">{inviting && <Loader2 size={14} className="animate-spin" />} Ajouter</button>
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-white/10 bg-card overflow-hidden">
                <div className="px-4 py-2.5 border-b border-white/[0.06] flex items-center gap-2"><Users size={15} className="text-zinc-400" /><span className="font-cabinet font-bold text-sm">Équipe</span><span className="text-xs text-zinc-500">{members.length}</span></div>
                {members.length === 0 ? <div className="p-8 text-center text-zinc-500 text-sm">Aucun membre.</div> : (
                  <div className="divide-y divide-white/[0.05]">
                    {members.map((m) => (
                      <div key={m.id} data-testid={`member-${m.id}`} className="flex items-center gap-3 px-4 py-3">
                        <Avatar name={m.name} color={ROLE_COLOR[m.role]} size={40} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate flex items-center gap-1.5">{m.name}{m.role === "owner" && <Crown size={12} className="text-amber-400" />}</div>
                          <div className="text-xs text-zinc-500 truncate">{m.email}</div>
                        </div>
                        {isOwner && m.role !== "owner" ? (
                          <>
                            <select data-testid={`member-role-${m.id}`} value={m.role} onChange={(e) => patchMember(m, { role: e.target.value })} className="fd-inline-select" style={{ color: ROLE_COLOR[m.role], background: ROLE_COLOR[m.role] + "1A" }}>
                              {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            <div className="hidden sm:flex items-center gap-1 text-xs text-zinc-400"><input data-testid={`member-rate-${m.id}`} type="number" defaultValue={m.hourly_rate || 0} onBlur={(e) => { const v = parseFloat(e.target.value) || 0; if (v !== (m.hourly_rate || 0)) patchMember(m, { hourly_rate: v }); }} className="w-16 fd-input !h-8" />€/h</div>
                            <button data-testid={`member-remove-${m.id}`} onClick={() => removeMember(m)} className="p-1.5 rounded-lg text-zinc-400 hover:text-signal hover:bg-signal/10"><Trash2 size={15} /></button>
                          </>
                        ) : (
                          <>
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ color: ROLE_COLOR[m.role], background: ROLE_COLOR[m.role] + "1A" }}>{roleLabel(m.role)}</span>
                            {m.hourly_rate > 0 && <span className="hidden sm:inline text-xs text-zinc-500">{m.hourly_rate} €/h</span>}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {!isOwner && <p className="text-xs text-zinc-500 mt-3 text-center">Seul le propriétaire de l'entreprise peut modifier les rôles et inviter des membres.</p>}
            </>
          )}
      </div>
    </div>
  );
}
